// ── AUDIT FINDINGS ───────────────────────────────────────────
// Single Audit findings and their corrective action plans: each finding, the
// CAP steps with an owner, a due date and the evidence that closes them, and
// the Corrective Action Plan and the Summary Schedule of Prior Audit Findings
// (2 CFR 200.511(c) and (b)) as Excel.
// Every count, status and alert is worked out on the server (lib/findings.js).
import { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";

// Same letters as lib/findings.js REQUIREMENTS.
const REQUIREMENTS = {
  A: "Activities allowed or unallowed", B: "Allowable costs/cost principles", C: "Cash management", D: "Reserved",
  E: "Eligibility", F: "Equipment and real property management", G: "Matching, level of effort, earmarking",
  H: "Period of performance", I: "Procurement and suspension and debarment", J: "Program income", K: "Reserved",
  L: "Reporting", M: "Subrecipient monitoring", N: "Special tests and provisions",
};
const STATUSES = [
  ["not-corrected", "Not corrected"], ["partial", "Partially corrected"],
  ["corrected", "Fully corrected"], ["no-longer-valid", "No longer valid"],
];
const RED = "#dc2626", GREEN = "#059669", AMBER = "#d97706";

const newId = () => Math.random().toString(36).slice(2, 12);
const blank = () => ({
  id: newId(), number: "", fiscalYear: new Date().getFullYear() - 1, title: "", requirement: "",
  program: { aln: "", name: "" }, agency: "", repeat: false, priorNumber: "", questionedCosts: 0,
  contact: { name: "", email: "" }, status: "not-corrected", statusNote: "", archived: false, disagree: false, disagreeReason: "", steps: [],
});
function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function FindingsPage() {
  const T = useT();
  const [findings, setFindings] = useState([]);
  const [editing, setEditing]   = useState(null);
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/findings`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load findings.");
      setFindings(data.findings || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setError("");
    try {
      const { progress, updatedAt, ...body } = editing;
      const res = await apiFetch(`${API}/findings/${encodeURIComponent(editing.id)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save.");
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete finding ${editing.number || ""}? This cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/findings/${encodeURIComponent(editing.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not delete.");
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const card  = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 18 };
  const label = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });
  const ui = { T, card, label, input, btn };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Audit Findings</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 20px" }}>
          Track each Single Audit finding to closure: corrective action steps with an owner, a due date and the evidence, then export the Corrective Action Plan and the Summary Schedule of Prior Audit Findings.
        </p>
        {error && <div role="alert" style={{ ...card, borderColor: RED, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}

        {editing
          ? <Editor {...ui} f={editing} setF={setEditing} onSave={save} onDelete={remove} onCancel={() => setEditing(null)} busy={busy} isNew={!findings.some((x) => x.id === editing.id)} />
          : <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button style={btn(true)} onClick={() => setEditing(blank())}>New finding</button>
              </div>
              <FindingList {...ui} findings={findings} onOpen={setEditing} />
              <Report {...ui} setError={setError} kind="corrective-action-plan" title="Corrective Action Plan"
                blurb="For each finding in the year being audited: the contact person responsible, the corrective action planned and the anticipated completion date, or the reasons you disagree (2 CFR 200.511(c))." />
              <Report {...ui} setError={setError} kind="summary-schedule" title="Summary Schedule of Prior Audit Findings"
                blurb="Lists every finding first reported before the fiscal year being audited, with its status and explanation (2 CFR 200.511(b)). Archived findings are left off." />
            </>}
      </div>
    </div>
  );
}

function FindingList({ T, card, findings, onOpen }) {
  if (!findings.length) return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>No findings yet. Add each finding from the auditor's schedule of findings and questioned costs.</div>;
  const th = { textAlign: "left", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: "10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["Finding", "Req.", "Program", "Status", "CAP steps", "Next due"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {findings.map((f) => {
            const p = f.progress || {};
            return (
              <tr key={f.id} onClick={() => onOpen(f)} onKeyDown={(e) => e.key === "Enter" && onOpen(f)} tabIndex={0} style={{ cursor: "pointer", opacity: f.archived ? 0.55 : 1 }}>
                <td style={td}><strong>{f.number}</strong>{f.repeat && <span style={{ color: AMBER, marginLeft: 6 }}>repeat</span>}<div style={{ color: T.textMuted, fontSize: 12 }}>{f.title}</div></td>
                <td style={td}>{f.requirement}</td>
                <td style={td}>{[f.program?.aln, f.program?.name].filter(Boolean).join(" ")}</td>
                <td style={td}>{(STATUSES.find(([k]) => k === f.status) || STATUSES[0])[1]}{f.archived && " (archived)"}</td>
                <td style={td}>
                  {p.done}/{p.total}
                  {p.overdue > 0 && <span style={{ color: RED, marginLeft: 6 }}>{p.overdue} overdue</span>}
                  {p.retestReady && <span style={{ color: GREEN, marginLeft: 6 }}>ready for retest</span>}
                </td>
                <td style={td}>{p.nextDue || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Editor({ T, card, label, input, btn, f, setF, onSave, onDelete, onCancel, busy, isNew }) {
  const set = (k, v) => setF({ ...f, [k]: v });
  const setIn = (k, sub, v) => setF({ ...f, [k]: { ...f[k], [sub]: v } });
  const setStep = (i, patch) => setF({ ...f, steps: f.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 14 };
  const field = (lbl, el) => <label style={{ display: "block" }}><span style={label}>{lbl}</span>{el}</label>;

  return (
    <div style={card}>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 16px" }}>{isNew ? "New finding" : `Finding ${f.number}`}</h2>
      <div style={grid}>
        {field("Finding number", <input style={input} value={f.number} onChange={(e) => set("number", e.target.value)} placeholder="2025-001" />)}
        {field("Fiscal year first reported", <input style={input} type="number" value={f.fiscalYear || ""} onChange={(e) => set("fiscalYear", e.target.value)} />)}
        {field("Compliance requirement", (
          <select style={input} value={f.requirement} onChange={(e) => set("requirement", e.target.value)}>
            <option value="">Choose…</option>
            {Object.entries(REQUIREMENTS).map(([k, v]) => <option key={k} value={k}>{k}. {v}</option>)}
          </select>
        ))}
        {field("Questioned costs ($)", <input style={input} value={f.questionedCosts} onChange={(e) => set("questionedCosts", e.target.value)} />)}
      </div>
      <div style={{ marginBottom: 14 }}>{field("Finding (short description)", <input style={input} value={f.title} onChange={(e) => set("title", e.target.value)} />)}</div>
      <div style={grid}>
        {field("Program ALN", <input style={input} value={f.program?.aln || ""} onChange={(e) => setIn("program", "aln", e.target.value)} placeholder="93.067" />)}
        {field("Program name", <input style={input} value={f.program?.name || ""} onChange={(e) => setIn("program", "name", e.target.value)} />)}
        {field("Federal agency", <input style={input} value={f.agency} onChange={(e) => set("agency", e.target.value)} />)}
      </div>
      <div style={grid}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
          <input type="checkbox" checked={f.repeat} onChange={(e) => set("repeat", e.target.checked)} /> Repeat finding
        </label>
        {f.repeat && field("Prior year finding number", <input style={input} value={f.priorNumber} onChange={(e) => set("priorNumber", e.target.value)} />)}
        {field("CAP contact name", <input style={input} value={f.contact?.name || ""} onChange={(e) => setIn("contact", "name", e.target.value)} />)}
        {field("CAP contact email", <input style={input} type="email" value={f.contact?.email || ""} onChange={(e) => setIn("contact", "email", e.target.value)} />)}
      </div>

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "20px 0 10px" }}>Corrective action steps</h3>
      {f.steps.map((s, i) => (
        <div key={s.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <div style={{ marginBottom: 10 }}>{field(`Step ${i + 1}`, <input style={input} value={s.action} onChange={(e) => setStep(i, { action: e.target.value })} placeholder="What will be done" />)}</div>
          <div style={grid}>
            {field("Owner", <input style={input} value={s.owner || ""} onChange={(e) => setStep(i, { owner: e.target.value })} />)}
            {field("Owner email (gets the alerts)", <input style={input} type="email" value={s.ownerEmail || ""} onChange={(e) => setStep(i, { ownerEmail: e.target.value })} />)}
            {field("Due", <input style={input} type="date" value={s.due || ""} onChange={(e) => setStep(i, { due: e.target.value })} />)}
          </div>
          <div style={grid}>
            {field("Evidence file name", <input style={input} value={s.evidence?.name || ""} onChange={(e) => setStep(i, { evidence: { ...s.evidence, name: e.target.value } })} />)}
            {field("Evidence link (Drive or OneDrive)", <input style={input} type="url" value={s.evidence?.url || ""} onChange={(e) => setStep(i, { evidence: { ...s.evidence, url: e.target.value } })} placeholder="https://" />)}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody, minHeight: 38 }}>
                <input type="checkbox" checked={!!s.done} onChange={(e) => setStep(i, { done: e.target.checked, doneOn: e.target.checked ? new Date().toISOString().slice(0, 10) : null })} /> Done
              </label>
              <button style={btn(false)} onClick={() => setF({ ...f, steps: f.steps.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          </div>
        </div>
      ))}
      <button style={btn(false)} onClick={() => setF({ ...f, steps: [...f.steps, { id: newId(), action: "", owner: "", ownerEmail: "", due: "", done: false, evidence: { name: "", url: "" } }] })}>Add step</button>

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "22px 0 10px" }}>For the Corrective Action Plan</h3>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody, marginBottom: 10 }}>
        <input type="checkbox" checked={!!f.disagree} onChange={(e) => set("disagree", e.target.checked)} /> We do not agree with this finding, or believe no corrective action is needed
      </label>
      {f.disagree && field("Specific reasons (2 CFR 200.511(c))", (
        <textarea style={{ ...input, minHeight: 70, resize: "vertical" }} value={f.disagreeReason || ""} onChange={(e) => set("disagreeReason", e.target.value)} />
      ))}

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "22px 0 10px" }}>For the Summary Schedule</h3>
      <div style={grid}>
        {field("Status", (
          <select style={input} value={f.status} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
          <input type="checkbox" checked={f.archived} onChange={(e) => set("archived", e.target.checked)} /> Reported as closed on an earlier schedule (leave off)
        </label>
      </div>
      {field("Corrective action taken, planned action and reason it recurred, or why it is no longer valid", (
        <textarea style={{ ...input, minHeight: 90, resize: "vertical" }} value={f.statusNote} onChange={(e) => set("statusNote", e.target.value)} />
      ))}

      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        <button style={btn(true)} onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Save finding"}</button>
        <button style={btn(false)} onClick={onCancel} disabled={busy}>Cancel</button>
        {!isNew && <button style={{ ...btn(false), color: RED, marginLeft: "auto" }} onClick={onDelete} disabled={busy}>Delete</button>}
      </div>
    </div>
  );
}

function Report({ T, card, label, input, btn, setError, kind, title, blurb }) {
  const [fy, setFy] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/findings/${kind}?fy=${encodeURIComponent(fy)}&format=json`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not build the schedule.");
      setPreview(data);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const download = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/findings/${kind}?fy=${encodeURIComponent(fy)}`);
      if (!res.ok) throw new Error((await res.json()).error || "Could not build the schedule.");
      saveFile(await res.blob(), `${kind === "summary-schedule" ? "summary-schedule-prior-audit-findings" : kind}-FY${fy}.xlsx`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <div style={card}>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 6px" }}>{title}</h2>
      <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, margin: "0 0 14px" }}>
        {blurb}
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label><span style={label}>Fiscal year being audited</span><input style={{ ...input, width: 140 }} type="number" value={fy} onChange={(e) => { setFy(e.target.value); setPreview(null); }} /></label>
        <button style={btn(false)} onClick={check} disabled={busy}>Check</button>
        <button style={btn(true)} onClick={download} disabled={busy}>Download Excel</button>
      </div>
      {preview && (
        <div style={{ marginTop: 14, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
          {preview.rows.length} finding{preview.rows.length === 1 ? "" : "s"} included.
          {preview.warnings.length
            ? <ul style={{ color: AMBER, margin: "8px 0 0", paddingLeft: 18 }}>{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            : <div style={{ color: GREEN, marginTop: 6 }}>Nothing missing.</div>}
        </div>
      )}
    </div>
  );
}
